import { Link } from "@tanstack/react-router";
import { motion } from "motion/react";
import { ChevronDown, LayoutDashboard, LineChart, LogIn, LogOut, ScrollText, Shield } from "lucide-react";
import { useProfileSync, useSession } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import logo from "@/assets/logo.png";


const nav = [
  { to: "/", label: "Papan" },
  { to: "/paket", label: "Paket Promosi" },
  { to: "/cara-kerja", label: "Cara Kerja" },
  { to: "/blog", label: "Blog" },
] as const;

const accountMenu = [
  { to: "/akun", label: "Dashboard Saya", icon: LayoutDashboard },
  { to: "/rekap", label: "Rekap", icon: LineChart },
  { to: "/riwayat", label: "Riwayat", icon: ScrollText },
] as const;

export function SiteHeader() {
  const session = useSession();
  useProfileSync(session);
  const initials = (session.name ?? session.email ?? "A").trim().charAt(0).toUpperCase();
  return (
    <header className="sticky top-0 z-40 border-b border-border/70 bg-background/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4">
        <Link to="/" className="flex items-center gap-2">
          <motion.span
            whileHover={{ rotate: -8, scale: 1.06 }}
            transition={{ type: "spring", stiffness: 340, damping: 16 }}
            className="grid size-9 place-items-center overflow-hidden rounded-xl border border-border/60 bg-card/70 backdrop-blur"
          >
            <img src={logo} alt="Logo jasasebar.com" width={36} height={36} className="size-7 object-contain" />
          </motion.span>
          <span className="font-display text-lg font-semibold tracking-tight">
            jasasebar<span className="text-muted-foreground">.com</span>
          </span>
        </Link>


        <nav className="hidden items-center gap-1 md:flex">
          {nav.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              activeProps={{ className: "bg-secondary text-foreground" }}
              className="rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            >
              {item.label}
            </Link>
          ))}
        </nav>



        <div className="flex items-center gap-2">
          {session.enabled &&
            (session.isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2 px-1.5" aria-label="Menu akun">
                    <Avatar className="size-7">
                      {session.picture && <AvatarImage src={session.picture} alt={session.name ?? "Foto profil"} />}
                      <AvatarFallback className="text-xs">{initials}</AvatarFallback>
                    </Avatar>
                    <span className="hidden max-w-28 truncate text-sm sm:inline">
                      {session.name ?? session.email}
                    </span>
                    <ChevronDown className="size-3.5 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="space-y-0.5">
                    <p className="truncate text-sm font-medium">{session.name ?? "Akun saya"}</p>
                    <p className="truncate text-xs font-normal text-muted-foreground">{session.email}</p>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {accountMenu.map((item) => (
                    <DropdownMenuItem key={item.to} asChild>
                      <Link to={item.to} className="cursor-pointer">
                        <item.icon className="size-4" />
                        {item.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                  {session.isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="cursor-pointer">
                          <Shield className="size-4" />
                          Admin
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={session.logout} className="cursor-pointer text-destructive focus:text-destructive">
                    <LogOut className="size-4" />
                    Keluar
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button size="sm" variant="secondary" onClick={session.login} disabled={session.isLoading}>
                <LogIn className="size-4" />
                <span className="hidden sm:inline">Masuk dengan Google</span>
              </Button>
            ))}
          <Link
            to="/"
            hash="daftar"
            className="rounded-xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-[var(--shadow-float)] transition-transform hover:-translate-y-0.5"
          >
            Sebar Sekarang
          </Link>
        </div>
      </div>
    </header>
  );
}

export function SiteFooter() {
  return (
    <footer className="mt-24 border-t border-border/70 bg-card/60">
      <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-10 text-sm text-muted-foreground md:flex-row md:items-center md:justify-between">
        <p>© {new Date().getFullYear()} jasasebar.com — papan promosi jasa paling ramai di Indonesia.</p>
        <div className="flex gap-4">
          <Link to="/paket" className="transition-colors hover:text-foreground">
            Paket Promosi
          </Link>
          <Link to="/cara-kerja" className="transition-colors hover:text-foreground">
            Cara Kerja
          </Link>
          <Link to="/blog" className="transition-colors hover:text-foreground">
            Blog
          </Link>
          <Link to="/tentang" className="transition-colors hover:text-foreground">
            Tentang Kami
          </Link>
          <Link to="/kontak" className="transition-colors hover:text-foreground">
            Kontak
          </Link>
          <Link to="/terms" className="transition-colors hover:text-foreground">
            Syarat &amp; Ketentuan
          </Link>
          <Link to="/privacy" className="transition-colors hover:text-foreground">
            Kebijakan Privasi
          </Link>
          <span>Pembayaran aman & terverifikasi</span>
        </div>
      </div>
    </footer>
  );
}
